import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { dataService } from 'src/services/dataService';
import { type ColorMode, type DisplayMode, getThemeConfig, type ThemeConfig } from 'src/utils/theme';

export type { ColorMode, DisplayMode, ThemeConfig } from 'src/utils/theme';

interface ThemeContextType {
  displayMode: DisplayMode;
  colorMode: ColorMode;
  theme: ThemeConfig;
  setDisplayMode: (mode: DisplayMode) => void;
  setColorMode: (mode: ColorMode) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [displayMode, setDisplayModeState] = useState<DisplayMode>('comfort');
  const [colorMode, setColorModeState] = useState<ColorMode>('light');
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async (): Promise<boolean> => {
    try {
      const user = await dataService.user.getCurrentUser();
      return !!user;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const isAuthenticated = await checkAuth();

        if (!isAuthenticated) {
          setDisplayModeState('comfort');
          setColorModeState('light');
          setIsLoading(false);
          return;
        }

        try {
          const profile = await dataService.profile.getCurrentUserProfile();
          const customFields = (profile?.custom_fields || {}) as Record<string, unknown>;
          const profileDisplayMode = customFields.displayMode as DisplayMode | undefined;
          const profileColorMode = customFields.colorMode as ColorMode | undefined;

          if (profileDisplayMode && ['comfort', 'compact', 'easy-reader'].includes(profileDisplayMode)) {
            setDisplayModeState(profileDisplayMode);
            localStorage.setItem('theme_display_mode', profileDisplayMode);
          } else {
            const savedDisplayMode = localStorage.getItem('theme_display_mode') as DisplayMode | null;
            if (savedDisplayMode && ['comfort', 'compact', 'easy-reader'].includes(savedDisplayMode)) {
              setDisplayModeState(savedDisplayMode);
            }
          }

          if (profileColorMode && ['light', 'dark'].includes(profileColorMode)) {
            setColorModeState(profileColorMode);
            localStorage.setItem('theme_color_mode', profileColorMode);
          } else {
            const savedColorMode = localStorage.getItem('theme_color_mode') as ColorMode | null;
            if (savedColorMode && ['light', 'dark'].includes(savedColorMode)) {
              setColorModeState(savedColorMode);
            } else {
              setColorModeState('light');
            }
          }
        } catch {
          const savedDisplayMode = localStorage.getItem('theme_display_mode') as DisplayMode | null;
          const savedColorMode = localStorage.getItem('theme_color_mode') as ColorMode | null;
          if (savedDisplayMode && ['comfort', 'compact', 'easy-reader'].includes(savedDisplayMode)) {
            setDisplayModeState(savedDisplayMode);
          }
          if (savedColorMode && ['light', 'dark'].includes(savedColorMode)) {
            setColorModeState(savedColorMode);
          } else {
            setColorModeState('light');
          }
        }
      } catch {
        setColorModeState('light');
        setDisplayModeState('comfort');
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();

    let authSubscription: { unsubscribe: () => void } | null = null;

    const setupAuthListener = async () => {
      try {
        const { getSupabase } = await import('src/utils/supabase');
        const supabase = await getSupabase();

        const { data } = supabase.auth.onAuthStateChange(async (_event, session) => {
          const isAuthenticated = !!session?.user;

          if (isAuthenticated) {
            const savedColorMode = localStorage.getItem('theme_color_mode') as ColorMode | null;
            const savedDisplayMode = localStorage.getItem('theme_display_mode') as DisplayMode | null;

            if (savedColorMode && ['light', 'dark'].includes(savedColorMode)) {
              setColorModeState(savedColorMode);
            } else {
              setColorModeState('light');
            }

            if (savedDisplayMode && ['comfort', 'compact', 'easy-reader'].includes(savedDisplayMode)) {
              setDisplayModeState(savedDisplayMode);
            }
          } else {
            setColorModeState('light');
            setDisplayModeState('comfort');
          }
        });

        authSubscription = data.subscription;
      } catch {}
    };

    setupAuthListener();

    return () => {
      if (authSubscription) authSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (colorMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [colorMode]);

  const persistToProfile = useCallback(async (fields: Record<string, unknown>) => {
    try {
      const user = await dataService.user.getCurrentUser();
      if (!user) return;
      await dataService.profile.updateProfile(user.id, { custom_fields: fields });
    } catch {}
  }, []);

  const setDisplayMode = useCallback(
    (mode: DisplayMode) => {
      setDisplayModeState(mode);
      localStorage.setItem('theme_display_mode', mode);
      persistToProfile({ displayMode: mode });
    },
    [persistToProfile]
  );

  const setColorMode = useCallback(
    (mode: ColorMode) => {
      setColorModeState(mode);
      localStorage.setItem('theme_color_mode', mode);
      persistToProfile({ colorMode: mode });
    },
    [persistToProfile]
  );

  const theme = useMemo(() => getThemeConfig(displayMode, colorMode), [displayMode, colorMode]);

  const value = useMemo(
    () => ({
      displayMode,
      colorMode,
      theme,
      setDisplayMode,
      setColorMode,
      isLoading,
    }),
    [displayMode, colorMode, theme, setDisplayMode, setColorMode, isLoading]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
